import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Alert } from '@/api/types';

/**
 * Health and anomaly findings, as ONE compact bar that expands on demand.
 *
 * It renders collapsed by design. Rendering every alert as a full card pushed the actual
 * dashboard below the fold — seven stacked warnings, several of them restating the same fact,
 * before a single number was visible. A wall of warnings is functionally the same as no warnings:
 * it gets scrolled past. One line that names the worst problem and counts the rest is read.
 */
export function AlertsStrip({ siteKey }: { siteKey: string }) {
  const [expanded, setExpanded] = useState(false);

  const { data, isError } = useQuery({
    queryKey: ['alerts', siteKey],
    queryFn: () => api.alerts(siteKey),
    staleTime: 60_000,
    retry: false,
  });

  // A failed read must not look like "no alerts": the bar disappearing is the all-clear signal.
  if (isError && !data) {
    return (
      <div className="alertbar alertbar-warning" role="status">
        <div className="alertbar-summary">
          <span className="alertbar-icon" aria-hidden="true">!</span>
          <span className="alertbar-title">Health checks could not be read</span>
          <span className="alertbar-counts t-label">alerts may be missing — reload in a moment</span>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) return null;

  const critical = data.filter((a) => a.severity === 'critical').length;
  const warnings = data.filter((a) => a.severity === 'warning').length;
  const info = data.length - critical - warnings;

  const worst = data[0]!; // API returns most-severe first
  const tone = toneOf(worst.severity);

  return (
    <div className={`alertbar alertbar-${tone}`}>
      <button
        type="button"
        className="alertbar-summary"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="alertbar-icon" aria-hidden="true">
          {worst.severity === 'critical' ? '✕' : worst.severity === 'warning' ? '!' : 'i'}
        </span>

        <span className="alertbar-title">{worst.title}</span>

        <span className="alertbar-counts t-label">
          {[
            critical ? `${critical} critical` : null,
            warnings ? `${warnings} warning${warnings === 1 ? '' : 's'}` : null,
            info ? `${info} info` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>

        <span className="alertbar-toggle t-label">{expanded ? 'Hide' : 'Details'}</span>
      </button>

      {expanded && (
        <div className="alertbar-list">
          {data.map((alert) => (
            <AlertRow key={alert.id} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  return (
    <div className="alertrow" role={alert.severity === 'critical' ? 'alert' : 'status'}>
      {/* Icon + label, never colour alone. */}
      <span className={`alertrow-dot alertrow-dot-${toneOf(alert.severity)}`} aria-hidden="true" />
      <div>
        <strong className="t-label">{alert.title}</strong>
        <div className="t-label muted">{alert.detail}</div>
        <div className="t-label" style={{ opacity: 0.8 }}>→ {alert.action}</div>
      </div>
    </div>
  );
}

function toneOf(severity: string): 'error' | 'warning' | 'info' {
  return severity === 'critical' ? 'error' : severity === 'warning' ? 'warning' : 'info';
}
